import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import { operatorApiFetch } from '@services/operator/apiService';
import { useOperatorAuth } from './OperatorAuthContext';

const QcOperatorContext = createContext();

export const QcOperatorProvider = ({ children }) => {
  const { operator: currentOperator } = useOperatorAuth();

  // QC 人員狀態
  const [qcOperator, setQcOperatorState] = useState(currentOperator);
  const [parcelOperator, setParcelOperatorState] = useState(currentOperator);
  const [operatorsVerified, setOperatorsVerified] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 當登入的操作員改變時，同步更新預設人員
  useEffect(() => {
    if (currentOperator && !qcOperator) {
      setQcOperatorState(currentOperator);
    }
    if (currentOperator && !parcelOperator) {
      setParcelOperatorState(currentOperator);
    }
  }, [currentOperator, qcOperator, parcelOperator]);

  // 驗證操作人員
  const verifyOperators = useCallback(async (qcAccount, parcelAccount) => {
    setLoading(true);
    setError(null);

    try {
      // 並行查詢兩個 operator
      const [qcResponse, parcelResponse] = await Promise.all([
        operatorApiFetch(`/operators/get_info?username=${encodeURIComponent(qcAccount)}`),
        operatorApiFetch(`/operators/get_info?username=${encodeURIComponent(parcelAccount)}`)
      ]);

      if (!qcResponse.ok) {
        const errorData = await qcResponse.json();
        throw new Error(errorData.message || 'operator_not_found');
      }

      if (!parcelResponse.ok) {
        const errorData = await parcelResponse.json();
        throw new Error(errorData.message || 'operator_not_found');
      }

      const qcData = await qcResponse.json();
      const parcelData = await parcelResponse.json();

      const qcOp = qcData.data;
      const parcelOp = parcelData.data;

      // 驗證兩個 operator 是否在同一個 warehouse
      if (qcOp.warehouse_id !== parcelOp.warehouse_id) {
        throw new Error('operators_not_same_warehouse');
      }

      setQcOperatorState(qcOp);
      setParcelOperatorState(parcelOp);
      setOperatorsVerified(true);

      return { qcOperator: qcOp, parcelOperator: parcelOp };
    } catch (err) {
      console.error('Failed to verify operators:', err);
      setError(err.message);
      setOperatorsVerified(false);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 設定 QC 人員
  const setQcOperator = useCallback((operator) => {
    setQcOperatorState(operator);
  }, []);

  // 設定包裝人員
  const setParcelOperator = useCallback((operator) => {
    setParcelOperatorState(operator);
  }, []);

  // 清除人員資訊
  const clearOperators = useCallback(() => {
    setQcOperatorState(null);
    setParcelOperatorState(null);
    setOperatorsVerified(false);
  }, []);

  const value = {
    qcOperator,
    parcelOperator,
    operatorsVerified,
    loading,
    error,
    verifyOperators,
    setQcOperator,
    setParcelOperator,
    clearOperators,
  };

  return (
    <QcOperatorContext.Provider value={value}>
      {children}
    </QcOperatorContext.Provider>
  );
};

export const useQcOperator = () => {
  const context = useContext(QcOperatorContext);
  if (!context) {
    throw new Error('useQcOperator must be used within a QcOperatorProvider');
  }
  return context;
};

export default QcOperatorContext;
